import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { X, Plus, ArrowRight, Check, TrendingUp, AlertTriangle } from "lucide-react";
import { formatCurrency, formatPercent } from "../lib/helpers";

const METRICS = [
  { key: "price", label: "Price", format: "currency", source: "property" },
  { key: "estimated_monthly_rent", label: "Est. Rent/mo", format: "currency" },
  { key: "monthly_mortgage", label: "Mortgage/mo", format: "currency" },
  { key: "cash_flow_monthly", label: "Cash Flow/mo", format: "currency", highlight: true },
  { key: "cap_rate", label: "Cap Rate", format: "percent" },
  { key: "deal_score", label: "Deal Score", format: "score" },
  { key: "one_percent_rule", label: "1% Rule", format: "bool" },
  { key: "fifty_percent_rule", label: "50% Rule", format: "bool" },
  { key: "brrrr_score", label: "BRRRR Score", format: "score" },
  { key: "roi_annual", label: "Annual ROI", format: "percent" },
];

function getVal(deal, key, source) {
  if (source === "property") return deal?.property?.[key];
  return deal?.[key];
}

function fmtVal(val, format) {
  if (val == null || val === undefined) return "—";
  switch (format) {
    case "currency": return formatCurrency(val);
    case "percent": return formatPercent(val);
    case "score": return `${Math.round(val)}/100`;
    case "bool": return val ? "Pass" : "Fail";
    default: return String(val);
  }
}

function getBest(deals, key, format, source) {
  const vals = deals.map(d => getVal(d, key, source)).filter(v => v != null);
  if (!vals.length) return null;
  if (format === "bool") return null;
  if (key === "monthly_mortgage" || key === "price") return Math.min(...vals);
  return Math.max(...vals);
}

export default function DealComparison({ deals = [], onRemove, onNavigate }) {
  const [selectedIds, setSelectedIds] = useState([]);

  const comparables = selectedIds.length > 0
    ? deals.filter(d => selectedIds.includes(d.property?.id))
    : [];

  const toggleDeal = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const clearAll = () => setSelectedIds([]);

  if (deals.length < 2) return null;

  return (
    <div data-testid="deal-comparison-section">
      {/* Selection strip */}
      {comparables.length === 0 && (
        <div className="mb-4 p-3 rounded-lg border border-dashed border-border bg-card/50" data-testid="comparison-prompt">
          <p className="text-xs text-muted-foreground text-center">
            Select 2-3 deals to compare side by side
          </p>
          <div className="flex flex-wrap gap-2 mt-2 justify-center">
            {deals.slice(0, 8).map(d => (
              <Button
                key={d.property?.id}
                variant={selectedIds.includes(d.property?.id) ? "default" : "outline"}
                size="sm"
                className="text-[10px] h-6 px-2"
                onClick={() => toggleDeal(d.property?.id)}
                data-testid={`compare-select-${d.property?.id}`}
              >
                <Plus className="w-2.5 h-2.5 mr-1" />
                {d.property?.address?.split(",")[0] || `Deal ${d.deal_score}`}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Comparison table */}
      {comparables.length >= 2 && (
        <Card className="mb-6 overflow-hidden" data-testid="comparison-table">
          <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-bold">Deal Comparison</CardTitle>
            <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={clearAll} data-testid="clear-comparison">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-2 text-muted-foreground font-medium w-32">Metric</th>
                    {comparables.map(d => (
                      <th key={d.property?.id} className="text-center p-2 min-w-[140px]">
                        <div className="flex flex-col items-center gap-1">
                          {d.property?.image_url && (
                            <img src={d.property.image_url} alt="" className="w-16 h-10 object-cover rounded" loading="lazy" />
                          )}
                          <span className="font-bold text-foreground truncate max-w-[130px]">
                            {d.property?.address?.split(",")[0] || "Property"}
                          </span>
                          <span className="text-[9px] text-muted-foreground">
                            {d.property?.city}, {d.property?.state}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost" size="sm" className="h-5 text-[9px] px-1.5"
                              onClick={(e) => { e.stopPropagation(); toggleDeal(d.property?.id); }}
                            >
                              <X className="w-2.5 h-2.5" />
                            </Button>
                            <Button
                              variant="ghost" size="sm" className="h-5 text-[9px] px-1.5"
                              onClick={() => onNavigate?.(d.property?.id)}
                              data-testid={`compare-view-${d.property?.id}`}
                            >
                              View <ArrowRight className="w-2.5 h-2.5 ml-0.5" />
                            </Button>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map(({ key, label, format, source, highlight }) => {
                    const best = getBest(comparables, key, format, source);
                    return (
                      <tr key={key} className={`border-b border-border/30 ${highlight ? 'bg-muted/20' : ''}`}>
                        <td className="p-2 font-medium text-muted-foreground">{label}</td>
                        {comparables.map(d => {
                          const val = getVal(d, key, source);
                          const isBest = best != null && val === best && comparables.length > 1;
                          const cellColor = format === "bool"
                            ? (val ? "text-emerald-500" : "text-red-500")
                            : format === "currency" && key === "cash_flow_monthly"
                              ? (val >= 0 ? "text-emerald-500" : "text-red-500")
                              : "text-foreground";

                          return (
                            <td key={d.property?.id} className={`p-2 text-center font-mono font-semibold ${cellColor}`}>
                              <div className="flex items-center justify-center gap-1">
                                {format === "bool" ? (
                                  val ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <X className="w-3.5 h-3.5 text-red-500" />
                                ) : (
                                  fmtVal(val, format)
                                )}
                                {isBest && <TrendingUp className="w-3 h-3 text-emerald-500 flex-shrink-0" />}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Verdict row */}
            <div className="p-3 bg-muted/30 border-t border-border">
              <p className="text-xs text-muted-foreground text-center">
                {(() => {
                  const best = comparables.reduce((a, b) =>
                    (a.deal_score || 0) > (b.deal_score || 0) ? a : b
                  );
                  const addr = best?.property?.address?.split(",")[0] || "Top deal";
                  return (
                    <span>
                      <strong className="text-emerald-500">{addr}</strong> scores highest at{" "}
                      <strong>{best?.deal_score}/100</strong> with{" "}
                      <strong>{formatCurrency(best?.cash_flow_monthly)}/mo</strong> cash flow
                    </span>
                  );
                })()}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selection count indicator */}
      {selectedIds.length > 0 && selectedIds.length < 2 && (
        <div className="mb-4 p-2 rounded-lg border border-amber-500/30 bg-amber-500/5 text-center" data-testid="comparison-hint">
          <p className="text-[10px] text-amber-500 flex items-center justify-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Select {2 - selectedIds.length} more deal{selectedIds.length === 0 ? "s" : ""} to compare
          </p>
        </div>
      )}
    </div>
  );
}
